/**
 * The PREBUILT catalog — the free front door's second step. Renders the ready-made
 * catalog companions with their pre-generated portraits and claims the picked one
 * by cloning her template (persona + pinned seed + reference + free gallery) into
 * the account: no generation job, no credit reservation, no `402`.
 *
 * This is what "Start with a ready-made companion / Free / Meet her now" must
 * reach. It deliberately does NOT render the describe/upload form — that form
 * ends in a paid reference build, which strands a fresh $0 account.
 * Presentation + one store call; zero domain logic.
 */

import { isOk } from '@4f/shared';
import { Button, MediaTile, Spinner, useT } from '@4f/ui';
import { For, type JSX, onMount, Show } from 'solid-js';
import type { CompanionView, PrebuiltTemplateView } from '../../api/types.ts';
import type { PrebuiltStore } from '../../stores/prebuilt.ts';
import { templateBlurb, templateLook } from './prebuilt-model.ts';

export interface PrebuiltCatalogProps {
  readonly store: PrebuiltStore;
  /** Called with the freshly cloned companion once she lands `ready`. */
  onCreated(companion: CompanionView): void;
}

export function PrebuiltCatalog(props: PrebuiltCatalogProps): JSX.Element {
  const { t } = useT();

  onMount(() => {
    void props.store.load();
  });

  const claim = async (templateId: string): Promise<void> => {
    const result = await props.store.claim(templateId);
    if (result && isOk(result)) {
      props.onCreated(result.value);
    }
  };

  return (
    <section class="f4-prebuilt" aria-label={t('builder.picker.prebuilt.title')}>
      <p class="f4-genui__prompt">{t('builder.picker.prebuilt.blurb')}</p>

      <Show when={props.store.loading() && props.store.templates().length === 0}>
        <Spinner label={t('builder.picker.prebuilt.title')} />
      </Show>
      <Show when={props.store.error()}>
        {(error) => (
          <p class="f4-genui__error" role="alert">
            {error().message}
          </p>
        )}
      </Show>

      <Show
        when={props.store.templates().length > 0}
        fallback={
          <Show when={!props.store.loading()}>
            <p class="f4-genui__hint">{t('surface.catalogEmpty')}</p>
          </Show>
        }
      >
        <ul class="f4-prebuilt__grid">
          <For each={props.store.templates()}>
            {(template) => (
              <li class="f4-prebuilt__card">
                <TemplateCard
                  template={template}
                  claiming={props.store.claiming() === template.templateId}
                  blocked={props.store.claiming() !== null}
                  onClaim={() => void claim(template.templateId)}
                />
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
}

interface TemplateCardProps {
  readonly template: PrebuiltTemplateView;
  readonly claiming: boolean;
  readonly blocked: boolean;
  onClaim(): void;
}

/** One catalog companion — her portrait, name, look line, blurb, and the free CTA. */
function TemplateCard(props: TemplateCardProps): JSX.Element {
  const { t } = useT();
  const look = (): string => templateLook(props.template);
  const blurb = (): string => templateBlurb(props.template);
  /**
   * The claim button's ACCESSIBLE name — the shared CTA plus whose card it is.
   *
   * The visible label is the same short string on every card, so a screen-reader
   * user tabbing the grid would otherwise hear "Start free, Start free, Start
   * free" with nothing to tell the companions apart (the portrait above it is
   * already named with her own name; the button was the odd one out). Composed
   * from the SAME translated CTA — no second key, no hardcoded English.
   *
   * The visible text is a PREFIX of it (WCAG 2.5.3 "Label in Name"), so a
   * speech-input user can still say what they see; the comma is read as a pause.
   */
  const claimLabel = (): string => `${t('builder.picker.prebuilt.cta')}, ${props.template.name}`;

  return (
    <>
      {/* The portrait is a second affordance for the same claim, named with her
          own name — a screen reader never meets a tile labelled with a raw id. */}
      <MediaTile
        kind="image"
        src={props.template.previewUrl ?? undefined}
        loading={props.template.previewUrl === null}
        alt={props.template.name}
        onSelect={() => {
          if (!props.blocked) {
            props.onClaim();
          }
        }}
      />
      <h3 class="f4-prebuilt__name">{props.template.name}</h3>
      <Show when={look()}>{(line) => <p class="f4-prebuilt__look">{line()}</p>}</Show>
      <Show when={blurb()}>{(line) => <p class="f4-prebuilt__blurb">{line()}</p>}</Show>
      <Button
        type="button"
        variant="primary"
        size="sm"
        aria-label={claimLabel()}
        disabled={props.blocked && !props.claiming}
        loading={props.claiming}
        loadingLabel={t('surface.working')}
        onClick={() => props.onClaim()}
      >
        {t('builder.picker.prebuilt.cta')}
      </Button>
    </>
  );
}
